import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Papa from "papaparse";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
    Document,
    Paragraph,
    Table,
    TableRow,
    TableCell,
    TextRun,
    Packer,
    WidthType,
    HeadingLevel,
    BorderStyle,
} from "docx";
import toast from "react-hot-toast";
import { omitKeys } from "state/helpers";

/* ===========================================================
 * Helpers
 * ===========================================================
 */

const ts = () =>
    new Date()
        .toISOString()
        .replace(/[-T:.Z]/g, "")
        .slice(0, 14);

const withTs = (name, ext) => `${name}_${ts()}.${ext}`;

const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";

    if (value instanceof Date)
        return value.toLocaleString();

    if (typeof value === "object")
        return JSON.stringify(value);

    return String(value);
};

function prepare(data = [], omitFields = []) {
    return data.map((item) => {
        const row = omitKeys(
            item,
            [...omitFields, "_id"]
        );

        const result = {};

        Object.entries(row).forEach(([key, value]) => {
            result[key] = normalizeValue(value);
        });

        return result;
    });
}

/* ===========================================================
 * XLSX
 * ===========================================================
 */

async function exportXlsx(
    data,
    fileName,
    omitFields
) {
    const rows = prepare(data, omitFields);

    if (!rows.length) return;

    const headers = Object.keys(rows[0]);

    const wb = new ExcelJS.Workbook();

    wb.creator = "DigitAir";
    wb.created = new Date();

    const ws = wb.addWorksheet("Data");

    ws.addRow(["No.", ...headers]);

    const headerRow = ws.getRow(1);

    headerRow.height = 24;

    headerRow.eachCell((cell) => {
        cell.font = {
            bold: true,
        };

        cell.alignment = {
            horizontal: "center",
            vertical: "middle",
        };

        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
                argb: "FFFFCC",
            },
        };

        cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
            bottom: { style: "thin" },
        };
    });

    rows.forEach((row, index) => {
        ws.addRow([
            index + 1,
            ...headers.map((h) => row[h]),
        ]);
    });

    ws.eachRow((row) => {
        row.eachCell((cell) => {
            cell.alignment = {
                wrapText: true,
                vertical: "middle",
            };

            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
                bottom: { style: "thin" },
            };
        });
    });

    ws.views = [
        {
            state: "frozen",
            ySplit: 1,
        },
    ];

    headers.forEach((header, index) => {
        const sample = rows.slice(0, 1000);

        const maxLength = Math.max(
            header.length,
            ...sample.map((row) =>
                String(row[header] ?? "").length
            )
        );

        ws.getColumn(index + 2).width =
            Math.min(maxLength + 2, 50);
    });

    ws.getColumn(1).width = 8;

    const buffer =
        await wb.xlsx.writeBuffer();

    saveAs(
        new Blob([buffer]),
        withTs(fileName, "xlsx")
    );
}

/* ===========================================================
 * CSV
 * ===========================================================
 */

function exportCsv(
    data,
    fileName,
    omitFields
) {
    const rows = prepare(data, omitFields);

    const csv = Papa.unparse(
        rows.map((row, index) => ({
            "No.": index + 1,
            ...row,
        }))
    );

    const BOM = "\uFEFF";

    saveAs(
        new Blob([BOM + csv], {
            type: "text/csv;charset=utf-8;",
        }),
        withTs(fileName, "csv")
    );
}

/* ===========================================================
 * PDF
 * ===========================================================
 */

function exportPdf(
    data,
    fileName,
    omitFields
) {
    const rows = prepare(data, omitFields);

    if (!rows.length) return;

    const headers = [
        "No.",
        ...Object.keys(rows[0]),
    ];

    const body = rows.map(
        (row, index) => [
            index + 1,
            ...Object.values(row),
        ]
    );

    const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
    });

    const margin = 10;

    doc.setFontSize(14);

    doc.text(fileName, margin, 10);

    const sampleRows = body.slice(0, 100);

    const colLengths = headers.map(
        (header, colIndex) =>
            Math.max(
                header.length,
                ...sampleRows.map((row) =>
                    String(
                        row[colIndex] ?? ""
                    ).length
                )
            )
    );

    const totalLength =
        colLengths.reduce(
            (sum, len) => sum + len,
            0
        );

    const tableWidth =
        doc.internal.pageSize.getWidth() -
        margin * 2;

    const columnStyles =
        Object.fromEntries(
            colLengths.map((len, index) => [
                index,
                {
                    cellWidth:
                        Math.max(
                            10,
                            (len / totalLength) *
                                tableWidth
                        ),
                },
            ])
        );

    autoTable(doc, {
        head: [headers],
        body,

        startY: 15,

        styles: {
            fontSize: 7,
            overflow: "linebreak",
            cellPadding: 1.5,
        },

        headStyles: {
            fillColor: [255, 255, 204],
            textColor: 0,
            fontStyle: "bold",
        },

        columnStyles,
    });

    doc.save(
        withTs(fileName, "pdf")
    );
}

/* ===========================================================
 * TXT
 * ===========================================================
 */

function exportText(
    data,
    fileName,
    omitFields
) {
    const rows = prepare(data, omitFields);

    if (!rows.length) return;

    const headers = [
        "No.",
        ...Object.keys(rows[0]),
    ];

    const lines = [
        headers.join("\t"),
        ...rows.map((row, index) =>
            [
                index + 1,
                ...Object.values(row),
            ].join("\t")
        ),
    ];

    saveAs(
        new Blob([lines.join("\n")], {
            type: "text/plain;charset=utf-8;",
        }),
        withTs(fileName, "txt")
    );
}

/* ===========================================================
 * HTML
 * ===========================================================
 */

function exportHtml(
    data,
    fileName,
    omitFields
) {
    const rows = prepare(data, omitFields);

    if (!rows.length) return;

    const headers = [
        "No.",
        ...Object.keys(rows[0]),
    ];

    const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${fileName}</title>
<style>
body {
    font-family: Arial, sans-serif;
}
table {
    border-collapse: collapse;
}
th, td {
    border: 1px solid #ccc;
    padding: 6px;
}
th {
    background: #ffffcc;
}
</style>
</head>
<body>

<h2>${fileName}</h2>

<table>
<thead>
<tr>
${headers
    .map((h) => `<th>${h}</th>`)
    .join("")}
</tr>
</thead>

<tbody>

${rows
    .map(
        (row, index) => `
<tr>
${[
    index + 1,
    ...Object.values(row),
]
    .map(
        (v) =>
            `<td>${v}</td>`
    )
    .join("")}
</tr>
`
    )
    .join("")}

</tbody>

</table>

</body>
</html>
`;

    saveAs(
        new Blob([html], {
            type: "text/html;charset=utf-8;",
        }),
        withTs(fileName, "html")
    );
}

/* ===========================================================
 * DOCX
 * ===========================================================
 */

async function exportDocx(
    data,
    fileName,
    omitFields
) {
    const rows = prepare(data, omitFields);

    if (!rows.length) return;

    const headers = [
        "No.",
        ...Object.keys(rows[0]),
    ];

    const tableRows = [];

    tableRows.push(
        new TableRow({
            tableHeader: true,

            children: headers.map(
                (header) =>
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: header,
                                        bold: true,
                                    }),
                                ],
                            }),
                        ],
                    })
            ),
        })
    );

    rows.forEach((row, index) => {
        tableRows.push(
            new TableRow({
                children: [
                    index + 1,
                    ...Object.values(row),
                ].map(
                    (value) =>
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun(
                                            String(
                                                value ?? ""
                                            )
                                        ),
                                    ],
                                }),
                            ],
                        })
                ),
            })
        );
    });

    const doc = new Document({
        creator: "DigitAir",

        title: fileName,

        sections: [
            {
                children: [
                    new Paragraph({
                        heading:
                            HeadingLevel.HEADING_1,

                        children: [
                            new TextRun(
                                fileName
                            ),
                        ],
                    }),

                    new Paragraph(""),

                    new Table({
                        width: {
                            size: 100,
                            type:
                                WidthType.PERCENTAGE,
                        },

                        borders: {
                            top: {
                                style:
                                    BorderStyle.SINGLE,
                                size: 1,
                            },
                            bottom: {
                                style:
                                    BorderStyle.SINGLE,
                                size: 1,
                            },
                            left: {
                                style:
                                    BorderStyle.SINGLE,
                                size: 1,
                            },
                            right: {
                                style:
                                    BorderStyle.SINGLE,
                                size: 1,
                            },
                            insideHorizontal:
                                {
                                    style:
                                        BorderStyle.SINGLE,
                                    size: 1,
                                },
                            insideVertical:
                                {
                                    style:
                                        BorderStyle.SINGLE,
                                    size: 1,
                                },
                        },

                        rows: tableRows,
                    }),
                ],
            },
        ],
    });

    const blob =
        await Packer.toBlob(doc);

    saveAs(
        blob,
        withTs(fileName, "docx")
    );
}

/* ===========================================================
 * Dispatcher
 * ===========================================================
 */

export async function exportData(
    data,
    fileName,
    format,
    omitFields = []
) {
    if (!data?.length) {
        toast.error(
            "No data to export."
        );
        return;
    }

    try {
        switch (format) {
            case "xlsx":
                return await exportXlsx(
                    data,
                    fileName,
                    omitFields
                );

            case "csv":
                return exportCsv(
                    data,
                    fileName,
                    omitFields
                );

            case "pdf":
                return exportPdf(
                    data,
                    fileName,
                    omitFields
                );

            case "text":
                return exportText(
                    data,
                    fileName,
                    omitFields
                );

            case "html":
                return exportHtml(
                    data,
                    fileName,
                    omitFields
                );

            case "docx":
                return await exportDocx(
                    data,
                    fileName,
                    omitFields
                );

            default:
                toast.error(
                    `Unknown format: ${format}`
                );
        }
    } catch (err) {
        console.error(err);

        toast.error(
            err?.message ||
                `Failed to export ${format}`
        );
    }
}

export const EXPORT_FORMATS = [
    {
        key: "xlsx",
        label: "Excel (.xlsx)",
    },
    {
        key: "csv",
        label: "CSV (.csv)",
    },
    {
        key: "pdf",
        label: "PDF (.pdf)",
    },
    {
        key: "docx",
        label: "Word (.docx)",
    },
    {
        key: "text",
        label: "Text (.txt)",
    },
    {
        key: "html",
        label: "HTML (.html)",
    },
];